import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ContentBlock } from 'site-modules/editorial/components/content-block/content-block';

export function TmvArticles({ blockClassName = '', titleClassName = '', withSeparators = false }) {
  const separator = withSeparators && <hr className="my-2_5 gradient-hr border-0" />;

  return (
    <Fragment>
      <ContentBlock
        items={[
          {
            text: `Edmunds True Market Value&reg; (TMV&reg;), also known as Edmunds Suggested Price, is Edmunds' estimate of the current average transaction price &mdash; that is, what others are paying &mdash; for new or used vehicles in your area so that you can begin your negotiations with a fair price in mind. For customers appraising their vehicles for sale, the Edmunds TMV is specifically for used vehicle trade-ins, to help you understand how much you might expect to receive when you sell your vehicle to a dealer.`,
            hasHTMLContent: true,
          },
          {
            text: `We determine those prices based on a variety of factors, including actual transaction data from dealers. CarMax is one of the dealers from which Edmunds obtains transaction data.`,
          },
          {
            text: `Edmunds has been a wholly owned subsidiary of CarMax, Inc. since June 2021. However, other than the incorporation of CarMax's transaction data by Edmunds into TMV&reg; calculations, CarMax does not participate in or otherwise influence the manner in which Edmunds computes those values.`,
            hasHTMLContent: true,
          },
        ]}
        title="A Note about Edmunds True Market Value&reg;"
        className={blockClassName}
        titleClassName={titleClassName}
      />
      {separator}
      <ContentBlock
        title="How to Use Edmunds Suggested Price"
        author={{
          title: 'by Ronald Montoya, Senior Consumer Advice Editor',
          link: '/about/authors/ronald-montoya.html',
        }}
        items={[
          {
            text: `If you've configured a vehicle using this page or the "build and price" option, you'll see the Edmunds Suggested Price (formerly called True Market Value) along with two other key pieces of information: the invoice price of the auto (roughly what the dealer paid for it) and the manufacturer's suggested retail price (MSRP), which is also known as the sticker price. You'll also see the suggested price when browsing a specific piece of new car inventory.`,
          },
          {
            text: `It is important to get the details of the year, make, model and options right or you may get incorrect results that can lead to misunderstandings at the dealership.`,
          },
          {
            text: `Used car appraisals will show different figures. More on that later.`,
          },
          {
            text: `The Edmunds Suggested Price is essential information for you as an auto shopper because cars sell at very different prices depending on their availability and popularity. Knowing this figure allows you to make an offer that a dealer will immediately recognize as reasonable. That means negotiations will take less time and be more relaxed. You'll also see the Edmunds Suggested Price on the Edmunds app and mobile site, so you can check TMV pricing while you're on the car lot.`,
          },
        ]}
        className={blockClassName}
        titleClassName={titleClassName}
      />
      {separator}
      <ContentBlock
        title="What Goes Into Edmunds Suggested Price"
        items={[
          {
            text: `For customers looking to purchase a vehicle, the Edmunds Suggested Price, also known as Edmunds True Market Value, is what we recommend you pay, not including taxes or fees. It is based on our analysis of millions of data points, including supply, demand, incentives, options and recent nearby transactions. It estimates a vehicle's average transaction price, not its "out the door" cost. This means it will not include incentives and fees that are typically applied after a transaction price has been determined. These include manufacturer-to-consumer rebates (such as dealer cash), sales tax, DMV fees and dealer documentation ("doc") fees. Here's more about <a href="https://www.edmunds.com/car-buying/what-fees-should-you-pay.html">new car fees</a>.`,
            hasHTMLContent: true,
          },
          {
            text: `We determine Edmunds Suggested Price based on a variety of factors, including actual transaction data from dealers. CarMax is one of the dealers from which Edmunds obtains transaction data.`,
          },
          {
            text: `Edmunds has been a wholly owned subsidiary of CarMax, Inc. since June 2021. However, other than the incorporation of CarMax's transaction data by Edmunds into TMV&reg; calculations, CarMax does not participate in or otherwise influence the manner in which Edmunds computes those values.`,
            hasHTMLContent: true,
          },
        ]}
        className={blockClassName}
        titleClassName={titleClassName}
      />
      {separator}
      <ContentBlock
        items={[
          {
            text: `Now that you know what the Edmunds Suggested Price is and where to find it, here are some tips to help you get a good deal.`,
          },
          {
            text: `The Edmunds Suggested Price is particularly useful when you use it in conjunction with a price quote from a dealer or when you're at the dealer to inquire about an auto you're interested in.`,
          },
          {
            text: `If the car price is greater than the suggested price, mention the figure to the salesperson when you're countering an offer. Say the salesperson had quoted you a price of $30,000, but the suggested price for the car is $28,000. Tell the salesperson: "I've had a chance to do some research on this car, and according to Edmunds, it's selling for about $28,000. If you can beat that price, we'll have a deal."`,
          },
          {
            text: `Other buyers just want to cut to the chase and will be satisfied with a fair deal, within reason. If this is your style, offer to pay the suggested price. It helps to have a screenshot or printout of the suggested price handy to show the salesperson you're not making things up.`,
          },
          {
            text: `There is no guarantee the salesperson will accept your offer to pay the Edmunds price. Edmunds data, while accurate, isn't infallible. There will be times when we don't have enough data to give accurate results or there is a trend that isn't being reflected in the numbers. In these cases, it's best to get your own "real-world" TMV. Talk to three dealerships to get price quotes. This tactic should give you an idea of the vehicle's actual selling price.`,
          },
        ]}
        title="Edmunds Suggested Price for New Car Shopping"
        className={blockClassName}
        titleClassName={titleClassName}
      />
      {separator}
      <ContentBlock
        title="Edmunds Suggested Price and Leasing"
        items={[
          {
            text: `While Edmunds does not offer specific prices for leasing, the suggested price of a car is still a useful piece of information if you plan to lease. A monthly lease payment is based on the selling price of the car, or the "cap cost," in leasing lingo. The lower the cost of the car, the lower the monthly lease payment will be. Once you determine the market value of a new car, you can enter that price into the Edmunds <a href="https://www.edmunds.com/calculators/car-lease.html">auto lease calculator</a>, along with other information about the car, to get an estimated lease payment.`,
            hasHTMLContent: true,
          },
        ]}
        className={blockClassName}
        titleClassName={titleClassName}
      />
      {separator}
      <ContentBlock
        title="Used Car Appraisal Pricing"
        items={[
          {
            text: `Edmunds has a handy <a href="https://www.edmunds.com/appraisal/">appraisal tool</a> for establishing the value of used cars. You'll want to know this value when the time comes to sell your vehicle or use it as a trade-in. This is called the "trade-in value." If you are buying a used car from a dealership, you also can look up its "dealer retail" price using the appraisal tool. The "private party" price is what you'd expect a private seller to ask for the car. If you're the seller, price your car slightly higher to leave room for negotiation.`,
            hasHTMLContent: true,
          },
          {
            text: `As you use the tool to get a fair market price, it's important to be accurate and honest about the options and the condition level of the used car. Most vehicles will be in "clean" or "average" condition. Very few will qualify for "outstanding" condition.`,
          },
          {
            text: `Edmunds also provides market prices for <a href="https://www.edmunds.com/car-buying/what-are-certified-used-vehicles.html">certified pre-owned cars</a>. Once you've entered a car's make, model, year, options, mileage and condition, the information for a certified pre-owned car displays at the bottom of the results page. Again, these are average prices and the final cost of the car is negotiable.`,
            hasHTMLContent: true,
          },
        ]}
        className={blockClassName}
        titleClassName={titleClassName}
      />
      {separator}
      <ContentBlock
        title="Keep Track of the Important Car Buying Figures"
        items={[
          {
            text: `You'll hear lots of car pricing terms tossed around when you're shopping and negotiating for a new car or used car. Amid all the talk of MSRP, sticker, savings and invoice, keep the Edmunds Suggested Price in mind. That's the number that will help put things into perspective.`,
          },
        ]}
        className={blockClassName}
        titleClassName={titleClassName}
      />
    </Fragment>
  );
}

TmvArticles.propTypes = {
  blockClassName: PropTypes.string,
  titleClassName: PropTypes.string,
  withSeparators: PropTypes.bool,
};
